import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiDollarSign, FiCheck } from 'react-icons/fi';
import { isAuthenticated } from '../../lib/auth';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import styles from './Dashboard.module.css';

interface WithdrawRequest {
  id: string;
  amount: number;
  cryptoAddress: string;
  cryptoNetwork: string | null;
  status: string;
  adminNote: string | null;
  createdAt: string;
  affiliate: {
    id: string;
    email: string;
    name: string;
  };
}

const Withdrawals: React.FC = () => {
  const navigate = useNavigate();
  const [withdrawRequests, setWithdrawRequests] = useState<WithdrawRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
  }, [navigate]);

  const fetchWithdrawals = async () => {
    try {
      const res = await api.get('/admin/withdraws', {
        params: {
          status: filterStatus !== 'all' ? filterStatus : undefined,
        },
      });
      setWithdrawRequests(res.data.withdrawRequests);
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const handleApprove = async (id: string) => {
    setActionLoading(id);
    try {
      await api.put(`/admin/withdraws/${id}/approve`);
      fetchWithdrawals();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to approve withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id: string) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return; // User cancelled

    setActionLoading(id);
    try {
      await api.put(`/admin/withdraws/${id}/reject`, { adminNote: reason });
      fetchWithdrawals();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to reject withdrawal');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkPaid = async (id: string) => {
    const note = window.prompt('Add a note (optional, e.g., transaction hash):');
    if (note === null) return; // User cancelled

    setActionLoading(id);
    try {
      await api.put(`/admin/withdraws/${id}/paid`, { adminNote: note });
      fetchWithdrawals();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to mark as paid');
    } finally {
      setActionLoading(null);
    }
  };

  const pendingCount = withdrawRequests.filter(w => w.status === 'PENDING').length;
  const pendingAmount = withdrawRequests
    .filter(w => w.status === 'PENDING')
    .reduce((sum, w) => sum + w.amount, 0);

  return (
    <AdminLayout>
      <div className={styles.contentHeader}>
        <div>
          <h1>Withdrawal Requests</h1>
          <p>Manage affiliate withdrawal requests</p>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#f59e0b' }}>
            <FiDollarSign />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>{pendingCount}</div>
            <div className={styles.statLabel}>Pending Requests</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: '#ef4444' }}>
            <FiDollarSign />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statValue}>${pendingAmount.toFixed(2)}</div>
            <div className={styles.statLabel}>Pending Amount</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className={styles.select}
        >
          <option value="all">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="PAID">Paid</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Affiliate</th>
                  <th>Amount</th>
                  <th>Network</th>
                  <th>Crypto Address</th>
                  <th>Status</th>
                  <th>Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {withdrawRequests.map((withdraw) => (
                  <tr key={withdraw.id}>
                    <td>{new Date(withdraw.createdAt).toLocaleString()}</td>
                    <td>
                      <div>{withdraw.affiliate.name}</div>
                      <small style={{ color: 'var(--text-muted)' }}>{withdraw.affiliate.email}</small>
                    </td>
                    <td className={styles.price}>${withdraw.amount.toFixed(2)}</td>
                    <td>
                      <span style={{ 
                        padding: '4px 8px',
                        background: 'var(--accent-dim)',
                        color: 'var(--accent-text)',
                        borderRadius: 'var(--r-sm)',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        fontFamily: 'var(--font-mono)'
                      }}>
                        {withdraw.cryptoNetwork || 'N/A'}
                      </span>
                    </td>
                    <td>
                      <code style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>
                        {withdraw.cryptoAddress.substring(0, 15)}...
                      </code>
                    </td>
                    <td>
                      <span className={styles[`status${withdraw.status}`] || styles.statusPending}>
                        {withdraw.status}
                      </span>
                    </td>
                    <td>{withdraw.adminNote || '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {withdraw.status === 'PENDING' && (
                          <>
                            <button
                              className={styles.actionBtn}
                              onClick={() => handleApprove(withdraw.id)}
                              disabled={actionLoading === withdraw.id}
                              style={{ background: '#10b981' }}
                            >
                              <FiCheck /> Approve
                            </button>
                            <button
                              className={styles.actionBtn}
                              onClick={() => handleReject(withdraw.id)}
                              disabled={actionLoading === withdraw.id}
                              style={{ background: '#ef4444' }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {withdraw.status === 'APPROVED' && (
                          <button
                            className={styles.actionBtn}
                            onClick={() => handleMarkPaid(withdraw.id)}
                            disabled={actionLoading === withdraw.id}
                            style={{ background: '#667eea' }}
                          >
                            <FiCheck /> Mark Paid
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {withdrawRequests.length === 0 && (
              <div className={styles.emptyState}>
                <p>No withdrawal requests found</p>
              </div>
          )}
        </>
      )}
      </div>
    </AdminLayout>
  );
};

export default Withdrawals;
